import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Link2, Send, Layers, X, RotateCcw, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { BLOCK_TYPES, pickImage } from "../blockDefs";
import { linkitApi, uploadLinkitImage, resolveImg, formatApiErrorDetail } from "../api";
import ImageCropperModal from "../../components/ImageCropperModal";

const BADGE_LIMIT = 25;
const DESCRIPTION_LIMIT = 120;
const TITLE_LIMIT = 90;

/* ------------------------------------------------------------------ ImageArea
 * Handles the image slot for both Link and Lead-form cards. Rules per spec:
 *   - No paste-URL input.
 *   - Show fetched image as thumbnail.
 *   - Actions: upload/replace, crop, restore original, remove.
 * ---------------------------------------------------------------- */
function ImageArea({ fetched, uploaded, onUploaded, onRestore, onRemove }) {
  const fileRef = useRef(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const current = uploaded || fetched || "";
  const canRestore = !!uploaded && !!fetched && uploaded !== fetched;

  const openCropper = (src) => setCropSrc(src);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (f) openCropper(URL.createObjectURL(f));
    e.target.value = "";
  };
  const editExisting = async () => {
    if (!current) return;
    try {
      const res = await fetch(resolveImg(current), { credentials: "omit" });
      const blob = await res.blob();
      openCropper(URL.createObjectURL(blob));
    } catch {
      toast.error("Couldn't load the image — upload a new one");
    }
  };
  const onCropped = async (blob) => {
    setCropSrc(null);
    setBusy(true);
    try {
      const url = await uploadLinkitImage(new File([blob], `card-${Date.now()}.jpg`, { type: blob.type || "image/jpeg" }));
      onUploaded(url);
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium">Image</Label>
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={editExisting}
          disabled={!current || busy}
          data-testid="link-image-thumb"
          title={current ? "Click to re-crop" : "No image yet"}
          className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-black/5 disabled:cursor-default"
        >
          {current ? (
            <>
              <img src={resolveImg(current)} alt="" className="h-full w-full object-cover" />
              <span className="absolute inset-0 hidden items-center justify-center bg-black/60 text-[10px] font-bold text-white group-hover:flex">
                Re-crop
              </span>
            </>
          ) : (
            <span className="flex h-full w-full items-center justify-center text-black/25">
              <Upload size={20} />
            </span>
          )}
        </button>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="link-image-upload" className="inline-flex items-center gap-1 rounded-lg border border-black/10 px-2.5 py-1.5 text-xs font-semibold hover:bg-black/5">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {current ? "Replace" : "Upload"}
          </button>
          {canRestore && (
            <button type="button" onClick={onRestore} data-testid="link-image-restore" className="inline-flex items-center gap-1 rounded-lg border border-black/10 px-2.5 py-1.5 text-xs font-semibold hover:bg-black/5" title="Use the image we fetched from the URL">
              <RotateCcw size={12} /> Original
            </button>
          )}
          {current && (
            <button type="button" onClick={onRemove} data-testid="link-image-remove" className="inline-flex items-center gap-1 rounded-lg border border-red-100 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100">
              <Trash2 size={12} /> Remove
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={pick} />
      </div>
      {cropSrc && (
        <ImageCropperModal src={cropSrc} defaultAspect={1} onCancel={() => setCropSrc(null)} onCropped={onCropped} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Link editor
 * URL-first flow: creator pastes a URL → we fetch OG metadata → autofill title,
 * description, image. Everything is editable before save. No CTA, no advanced
 * fields, no image URL box.
 * ---------------------------------------------------------------- */
function LinkEditor({ open, block, onClose, onSave }) {
  const [form, setForm] = useState(BLOCK_TYPES.link.defaults);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const initialUrlRef = useRef("");

  useEffect(() => {
    if (!open) return;
    const base = { ...BLOCK_TYPES.link.defaults };
    // Also accept legacy shape (label/url/thumbnail_url from old link blocks,
    // or product blocks migrated to link).
    if (block) {
      base.destination_url = block.destination_url || block.url || block.shop_url || "";
      base.fetched_image_url = block.fetched_image_url || "";
      base.uploaded_image_url = block.uploaded_image_url || block.thumbnail_url || block.image_url || "";
      base.title = block.title || block.label || block.name || "";
      base.description = block.description || "";
      base.badge = block.badge || block.discount_label || block.highlight || "";
    }
    setForm(base);
    initialUrlRef.current = base.destination_url;
    setFetchError(null);
  }, [open, block]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchPreview = async (rawUrl) => {
    const u = (rawUrl || "").trim();
    if (!u) return;
    setFetching(true);
    setFetchError(null);
    try {
      const { data } = await linkitApi.post("/page/og-preview", { url: u });
      // Only override fields the creator hasn't already customised.
      setForm((f) => ({
        ...f,
        destination_url: data.url || u,
        fetched_image_url: data.image || "",
        title: f.title && f.title.trim() ? f.title : (data.title || ""),
        description: f.description && f.description.trim() ? f.description : (data.description || ""),
      }));
      if (!data.title && !data.image) {
        setFetchError("Couldn't find a preview for that link — fill in the details manually.");
      }
    } catch (e) {
      setFetchError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't fetch that URL — fill in the details manually.");
    } finally {
      setFetching(false);
    }
  };

  // Debounced auto-fetch when the creator finishes typing (blur) or pastes.
  const onUrlBlur = () => {
    const u = (form.destination_url || "").trim();
    if (u && u !== initialUrlRef.current) fetchPreview(u);
  };

  const save = () => {
    if (!form.destination_url.trim()) { toast.error("Destination URL is required"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    onSave({
      destination_url: form.destination_url.trim(),
      fetched_image_url: form.fetched_image_url || "",
      uploaded_image_url: form.uploaded_image_url || "",
      title: form.title.trim().slice(0, TITLE_LIMIT),
      description: (form.description || "").trim().slice(0, DESCRIPTION_LIMIT),
      badge: (form.badge || "").trim().slice(0, BADGE_LIMIT),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto bg-white text-black">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 size={16} /> {block?.id ? "Edit link" : "Add a link"}</DialogTitle>
          <DialogDescription>Paste the URL and we&apos;ll pull the image, title and description automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Destination URL</Label>
            <div className="relative">
              <Input
                type="url"
                inputMode="url"
                placeholder="https://example.com/product"
                value={form.destination_url}
                onChange={(e) => set("destination_url", e.target.value)}
                onBlur={onUrlBlur}
                onPaste={(e) => setTimeout(() => fetchPreview(e.clipboardData.getData("text")), 0)}
                data-testid="link-url"
                className="pr-10"
              />
              {fetching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-indigo-500" />}
            </div>
            {fetchError && <p className="text-[11px] text-amber-700" data-testid="link-fetch-error">{fetchError}</p>}
          </div>

          <ImageArea
            fetched={form.fetched_image_url}
            uploaded={form.uploaded_image_url}
            onUploaded={(url) => set("uploaded_image_url", url)}
            onRestore={() => set("uploaded_image_url", "")}
            onRemove={() => setForm((f) => ({ ...f, uploaded_image_url: "", fetched_image_url: "" }))}
          />

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value.slice(0, TITLE_LIMIT))} data-testid="link-title" placeholder="What is this link?" />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between"><Label className="text-xs font-medium">Description</Label><span className="text-[10px] text-black/40">optional · {DESCRIPTION_LIMIT - (form.description?.length || 0)} left</span></div>
            <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value.slice(0, DESCRIPTION_LIMIT))} data-testid="link-description" placeholder="One short line about it (optional)" />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between"><Label className="text-xs font-medium">Badge / sticker</Label><span className="text-[10px] text-black/40">optional · {BADGE_LIMIT - (form.badge?.length || 0)} left</span></div>
            <Input value={form.badge} onChange={(e) => set("badge", e.target.value.slice(0, BADGE_LIMIT))} data-testid="link-badge" placeholder="e.g. 10% off, My favourite, New" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm hover:bg-black/5">Cancel</button>
          <button onClick={save} data-testid="link-save" className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-400">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ Lead form editor
 * A dead-simple "which fields do I want" toggler + basic copy.
 * ---------------------------------------------------------------- */
const LEAD_FIELDS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone number" },
  { key: "message", label: "Message" },
];

function LeadFormEditor({ open, block, onClose, onSave }) {
  const [form, setForm] = useState(BLOCK_TYPES.lead_form.defaults);

  useEffect(() => {
    if (!open) return;
    const base = { ...BLOCK_TYPES.lead_form.defaults };
    if (block) {
      base.title = block.title || block.heading || base.title;
      base.description = block.description || block.subtext || "";
      base.badge = block.badge || "";
      base.uploaded_image_url = block.uploaded_image_url || "";
      base.success_message = block.success_message || base.success_message;
      // Merge saved fields on top of defaults so newly-added fields stay usable.
      base.form_configuration = { ...base.form_configuration, ...(block.form_configuration || {}) };
    }
    setForm(base);
  }, [open, block]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setField = (k, patch) =>
    setForm((f) => ({
      ...f,
      form_configuration: { ...(f.form_configuration || {}), [k]: { ...(f.form_configuration?.[k] || {}), ...patch } },
    }));

  const save = () => {
    if (!form.title.trim()) { toast.error("Form title is required"); return; }
    // At least one field must be enabled so submissions are meaningful.
    const anyEnabled = Object.values(form.form_configuration || {}).some((v) => v?.enabled);
    if (!anyEnabled) { toast.error("Enable at least one field"); return; }
    onSave({
      title: form.title.trim().slice(0, TITLE_LIMIT),
      description: (form.description || "").trim().slice(0, DESCRIPTION_LIMIT),
      badge: (form.badge || "").trim().slice(0, BADGE_LIMIT),
      uploaded_image_url: form.uploaded_image_url || "",
      form_configuration: form.form_configuration,
      success_message: (form.success_message || "").trim() || "Thanks — I'll be in touch soon.",
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto bg-white text-black">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send size={16} /> {block?.id ? "Edit lead form" : "Add a lead form"}</DialogTitle>
          <DialogDescription>Collect contact info, consultations, brand enquiries — anything.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Form title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value.slice(0, TITLE_LIMIT))} data-testid="lf-title" placeholder="e.g. Book a consultation" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">One-line description (optional)</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value.slice(0, DESCRIPTION_LIMIT))} data-testid="lf-description" />
          </div>

          <ImageArea
            fetched=""
            uploaded={form.uploaded_image_url}
            onUploaded={(url) => set("uploaded_image_url", url)}
            onRestore={() => set("uploaded_image_url", "")}
            onRemove={() => set("uploaded_image_url", "")}
          />

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Badge (optional)</Label>
            <Input value={form.badge} onChange={(e) => set("badge", e.target.value.slice(0, BADGE_LIMIT))} data-testid="lf-badge" placeholder="e.g. Free 15 min call" />
          </div>

          <div className="space-y-2 rounded-xl border border-black/10 p-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-black/50">Fields to collect</p>
            {LEAD_FIELDS.map((f) => {
              const cfg = form.form_configuration?.[f.key] || {};
              return (
                <div key={f.key} className="flex items-center justify-between rounded-lg bg-black/[0.02] px-3 py-2">
                  <div>
                    <p className="text-sm font-semibold">{f.label}</p>
                    <p className="text-[11px] text-black/50">{cfg.enabled ? (cfg.required ? "Shown · required" : "Shown · optional") : "Hidden"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold">
                      <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setField(f.key, { enabled: e.target.checked, required: e.target.checked ? cfg.required : false })} data-testid={`lf-field-${f.key}-enabled`} />
                      Show
                    </label>
                    <label className={`inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold ${cfg.enabled ? "" : "opacity-40"}`}>
                      <input type="checkbox" disabled={!cfg.enabled} checked={!!cfg.required} onChange={(e) => setField(f.key, { required: e.target.checked })} data-testid={`lf-field-${f.key}-required`} />
                      Required
                    </label>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Success message</Label>
            <Input value={form.success_message} onChange={(e) => set("success_message", e.target.value)} data-testid="lf-success" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm hover:bg-black/5">Cancel</button>
          <button onClick={save} data-testid="lf-save" className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-400">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ Section editor
 * Just title + subtitle + default-open toggle.
 * ---------------------------------------------------------------- */
function SectionEditor({ open, block, onClose, onSave }) {
  const [form, setForm] = useState(BLOCK_TYPES.section.defaults);
  useEffect(() => {
    if (!open) return;
    setForm({ ...BLOCK_TYPES.section.defaults, ...(block || {}) });
  }, [open, block]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = () => {
    if (!form.title.trim()) { toast.error("Group title is required"); return; }
    onSave({ title: form.title.trim(), subtitle: (form.subtitle || "").trim(), default_open: !!form.default_open });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white text-black">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers size={16} /> {block?.id ? "Edit group" : "Add a group"}</DialogTitle>
          <DialogDescription>Give a group of cards a title so visitors can find them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs font-medium">Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="sec-title" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-medium">Subtitle (optional)</Label><Input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} data-testid="sec-subtitle" /></div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm hover:bg-black/5">Cancel</button>
          <button onClick={save} data-testid="sec-save" className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-400">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ Root
 * Route to the right editor based on block.type. Legacy types (product,
 * contact, email_capture, image, video, gallery, socials, text, share) get
 * mapped to the closest new type on first edit so the whole page eventually
 * converges on the simplified model.
 * ---------------------------------------------------------------- */
export default function BlockEditor({ open, block, onClose, onSave }) {
  if (!block) return null;
  // Legacy contact/email_capture/newsletter → lead_form. Legacy product/image/
  // video/gallery/socials/text/share → link (best-effort).
  let type = block.type;
  if (type === "contact" || type === "email_capture") type = "lead_form";
  else if (["product", "image", "video", "gallery", "text", "socials", "share"].includes(type)) type = "link";

  const handleSave = (patch) => onSave({ type, ...patch });

  if (type === "lead_form") return <LeadFormEditor open={open} block={block} onClose={onClose} onSave={handleSave} />;
  if (type === "section") return <SectionEditor open={open} block={block} onClose={onClose} onSave={handleSave} />;
  return <LinkEditor open={open} block={block} onClose={onClose} onSave={handleSave} />;
}

// Re-export the image-picker helper so PagePreview can stay in sync.
export { pickImage };
